import { Fragment } from "react";
import { motion } from "framer-motion";
import { CalendarPlus, CalendarOff, MapPin, Users } from "lucide-react";
import { Card } from "@/components/ui/Card";
import { TechAvatar } from "@/components/TechAvatar";
import { DispatchBlock } from "./DispatchBlock";
import { PegasusBoardBlock } from "./PegasusBoardBlock";
import type { TechLane } from "./api";
import type { PendingTicket } from "./queue";
import type { Dispatch } from "@/lib/types";
import { AVAIL_KIND_LABEL, type AvailEvent, availOutLabel } from "./availability";
import { cn } from "@/lib/cn";

interface Props {
  lane: TechLane;
  index: number;
  onSelect: (d: Dispatch) => void;
  /** This tech's availability for the board date (TECH-AVAIL-01). */
  events?: { out: AvailEvent | null; timed: AvailEvent[] };
  /** Open the time-off / meeting modal for this tech. */
  onAddAvailability?: (empno: string) => void;
  /** Open the modal in EDIT mode for a specific entry (click its block). */
  onEditAvailability?: (e: AvailEvent) => void;
  /** Open the rebuild-the-day modal (move an OUT tech's calls). */
  onRebuildDay?: (empno: string) => void;
  /** SCHED-02: Pegasus-scheduled jobs for this tech on the board date. */
  pegasusJobs?: PendingTicket[];
  /** Click a Pegasus job → board detail (cockpit strip / popup), like ESC. */
  onSelectTicket?: (job: PendingTicket) => void;
  /** Live drag preview — which lane + before which item the dragged card lands. */
  dragPreview?: { empno: string; beforeKey: string | null } | null;
  onDragPreview?: (p: { empno: string; beforeKey: string | null } | null) => void;
  /** Board-level "set window?" prompt (keyed by job id; survives a cross-tech move). */
  windowPrompt?: { jobId: string; window: string } | null;
  onWindowPrompt?: (p: { jobId: string; window: string } | null) => void;
  /** Board density: compact = ESC-style color blocks (whole roster on screen). */
  density?: "compact" | "cards";
  /** This column is the expanded one (in-place — others stay visible). */
  expanded?: boolean;
  /** Click the tech's name to expand just their column (clears on re-click). */
  onToggleTech?: (empno: string) => void;
  /**
   * Cockpit fit mode: lanes are narrow, so show just the FIRST name in the
   * header (full name truncates to nothing). Also flips the header click's
   * intent from "expand this column" to "filter to this tech" (the caller
   * wires onToggleTech to the global filter), so the title says "Show only".
   */
  firstNameOnly?: boolean;
  /** The day the board is showing — passed to cards so a Pegasus job dated
   *  earlier renders as a flagged CARRYOVER ("from {date}"). */
  boardDate?: string;
}

/** Approx start-minute of a Pegasus arrival window, for chronological sort. */
function pegasusWindowMinutes(w: string | null): number {
  if (!w) return -1;
  const m: Record<string, number> = {
    "8AM": 8 * 60,
    "8-12": 8 * 60,
    "7-8": 7 * 60, // install window (Seth 2026-06-30)
    "10-2": 10 * 60,
    "11-1": 11 * 60, // install window (Seth 2026-06-30)
    "12-4": 12 * 60,
    "3-7": 15 * 60,
    After: 17 * 60,
  };
  return m[w] ?? 12 * 60;
}

/** Sort-minute for a Pegasus job — the EXACT arrival time wins (BOARD-PLACE: it
 *  sequences between two calls), falling back to the window's start. */
function pegasusMinutes(j: { scheduled_time: string | null; scheduled_window: string | null }): number {
  if (j.scheduled_time) {
    const h = parseInt(j.scheduled_time.slice(0, 2), 10);
    const m = parseInt(j.scheduled_time.slice(3, 5), 10);
    if (Number.isFinite(h) && Number.isFinite(m)) return h * 60 + m;
  }
  return pegasusWindowMinutes(j.scheduled_window);
}

/** OUT banner colors per availability kind. */
function kindClasses(kind: AvailEvent["kind"]): string {
  switch (kind) {
    case "sick":
      return "bg-rose-100 text-rose-800";
    case "vacation":
      return "bg-sky-100 text-sky-800";
    case "meeting":
      return "bg-amber-100 text-amber-800";
    case "errand":
      return "bg-amber-50 text-amber-700";
    case "training":
      return "bg-violet-100 text-violet-800";
    default:
      return "bg-slate-200 text-slate-700";
  }
}

/** In-column block colors per kind (dashed cards in the day's flow). */
function blockClasses(kind: AvailEvent["kind"]): string {
  switch (kind) {
    case "sick":
      return "border-rose-300 bg-rose-50 text-rose-900";
    case "vacation":
      return "border-sky-300 bg-sky-50 text-sky-900";
    case "meeting":
      return "border-amber-300 bg-amber-50 text-amber-900";
    case "errand":
      return "border-amber-200 bg-amber-50/70 text-amber-800";
    case "training":
      return "border-violet-300 bg-violet-50 text-violet-900";
    default:
      return "border-slate-300 bg-slate-100 text-slate-700";
  }
}

function timeLabel(e: AvailEvent): string {
  const t = (iso: string) =>
    new Date(iso).toLocaleTimeString(undefined, {
      hour: "numeric",
      minute: "2-digit",
    });
  const end = new Date(e.end_at);
  // An end at/after 11:59 PM = "rest of the day" (the No-more-calls marker).
  if (end.getHours() === 23 && end.getMinutes() >= 59)
    return `from ${t(e.start_at)}`;
  return `${t(e.start_at)} – ${t(e.end_at)}`;
}

/**
 * A timed availability block rendered IN the column's day flow at its
 * chronological position — meetings, errands, "no more calls" — so dispatchers
 * see it exactly where it sits in the tech's day, the way ESC's off-tickets
 * did. Click → the availability modal (view / cancel / add).
 */
function AvailabilityBlock({
  event,
  onClick,
}: {
  event: AvailEvent;
  onClick?: () => void;
}) {
  const Icon = event.kind === "meeting" ? Users : CalendarOff;
  return (
    <button
      type="button"
      onClick={onClick}
      title="Blocked time — click to manage"
      className={cn(
        "block w-full rounded-md border-2 border-dashed px-3 py-2 text-left transition-colors hover:brightness-[0.98]",
        blockClasses(event.kind),
      )}
    >
      <div className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider">
        <Icon className="h-3 w-3" />
        {AVAIL_KIND_LABEL[event.kind]}
        <span className="normal-case tracking-normal">{timeLabel(event)}</span>
      </div>
      {event.title && (
        <div className="mt-0.5 truncate text-xs font-semibold">
          {event.title}
        </div>
      )}
      {event.location && (
        <div className="mt-0.5 flex items-center gap-1 truncate text-[11px] opacity-80">
          <MapPin className="h-3 w-3" />
          {event.location}
        </div>
      )}
    </button>
  );
}

/** Minutes-since-midnight for sorting calls + events into one day flow. */
function callMinutes(d: Dispatch): number {
  if (!d.tpromtime) return -1; // untimed calls stay at the top (current behavior)
  const h = parseInt(d.tpromtime.slice(0, 2), 10);
  const m = parseInt(d.tpromtime.slice(3, 5), 10);
  return Number.isFinite(h) && Number.isFinite(m) ? h * 60 + m : -1;
}
function eventMinutes(e: AvailEvent): number {
  const dt = new Date(e.start_at);
  return dt.getHours() * 60 + dt.getMinutes();
}

export function TechColumn({
  lane,
  onSelect,
  events,
  onAddAvailability,
  onEditAvailability,
  onRebuildDay,
  pegasusJobs,
  onSelectTicket,
  dragPreview,
  onDragPreview,
  windowPrompt,
  onWindowPrompt,
  density = "cards",
  expanded = false,
  onToggleTech,
  firstNameOnly = false,
  boardDate,
}: Props) {
  const out = events?.out ?? null;
  const timed = events?.timed ?? [];
  const hasOpenCalls = lane.stops.length > 0 && lane.doneCount < lane.totalStops;
  // Live drop target highlight while a card is dragged over this column.
  const targeted = dragPreview?.empno === lane.empno;
  const dropLine = <div className="my-0.5 h-1.5 rounded-full bg-brand-500" aria-hidden />;

  // One chronological day flow: customer calls + availability blocks together.
  const items: { key: string; t: number; node: React.ReactNode }[] = [
    ...lane.stops.map((d) => ({
      key: `d-${d.dispatch}-${d.counter}`,
      t: callMinutes(d),
      node: <DispatchBlock dispatch={d} onClick={onSelect} density={density} />,
    })),
    ...timed.map((e) => ({
      key: `a-${e.id}`,
      t: eventMinutes(e),
      node: (
        <AvailabilityBlock
          event={e}
          onClick={() =>
            onEditAvailability
              ? onEditAvailability(e)
              : onAddAvailability?.(lane.empno)
          }
        />
      ),
    })),
    ...(pegasusJobs ?? []).map((j) => ({
      key: `p-${j.id}`,
      t: pegasusMinutes(j),
      node: <PegasusBoardBlock job={j} density={density} onSelect={onSelectTicket} onDragPreview={onDragPreview} windowPrompt={windowPrompt} onWindowPrompt={onWindowPrompt} boardDate={boardDate} />,
    })),
  ].sort((a, b) => a.t - b.t);

  return (
    // initial={false}: entrance animation only ever played on real first
    // mounts anyway, but any lane-set change re-ran the stagger and made a
    // simple expand/filter FEEL like a page reload (Seth). Columns now just
    // be there; width changes animate via the grid transition instead.
    // data-drop-empno/-tech-name: the drag-drop target AND the double-click-to-
    // create resolver (BoardView finds the nearest lane by these rects).
    <motion.div initial={false} className="flex min-w-0 flex-col" data-drop-empno={lane.empno} data-tech-name={lane.name}>
      <Card
        className={cn(
          // No overflow-hidden: a dragged card must stay VISIBLE as it crosses
          // into another column (Seth 2026-06-24 — couldn't see/do tech moves).
          "flex flex-col transition-shadow",
          (lane.isOff || out) && "opacity-80",
          out && "ring-1 ring-rose-200",
          expanded && "ring-2 ring-brand-300 shadow-e2",
          targeted && "ring-2 ring-brand-400 shadow-e2",
        )}
      >
        {/* Tech header. BOARD-NAMES-01 (Seth 2026-10-06, MacBook screenshot:
            "A..", "B.." — "I don't love how the names are cut off"): in narrow
            lanes (compact density / cockpit fit) the one-row header let the
            score pill + block-time button squeeze the NAME to two letters.
            Narrow lanes now use a TWO-ROW header — the name gets the whole
            first row, the chrome moves below it — so the tech is readable at
            every width. Wide cards lanes keep the familiar single row. */}
        <div className="rounded-t-xl border-b border-hairline bg-slate-50 px-3 py-2">
          {(() => {
            const narrow = density === "compact" || firstNameOnly;
            const nameBtn = (
              <button
                type="button"
                onClick={() => onToggleTech?.(lane.empno)}
                disabled={!onToggleTech}
                title={
                  !onToggleTech
                    ? lane.name
                    : firstNameOnly
                      ? `${lane.name} — show only their calls (click again to show everyone)`
                      : `${lane.name} — expand this column (click again to show everyone)`
                }
                className={cn(
                  "flex min-w-0 items-center gap-2 rounded-md text-left",
                  onToggleTech && "transition-colors hover:bg-slate-100",
                )}
              >
                <TechAvatar empno={lane.empno} name={lane.name} size="sm" />
                <h3 className={cn("truncate font-bold tracking-tight text-slate-900", narrow ? "text-[13px]" : "text-[14px]")}>
                  {lane.name}
                </h3>
              </button>
            );
            const chrome = (
              <div className="flex flex-shrink-0 items-center gap-1.5">
                {/* BOARD-HD: the lane score as a real pill — green when the day
                    is finished, so a cleared column reads at a glance. */}
                <span
                  title={`${lane.doneCount} of ${lane.totalStops} done`}
                  className={cn(
                    "rounded-full px-2 py-0.5 text-[11px] font-bold tabular-nums ring-1",
                    lane.totalStops > 0 && lane.doneCount >= lane.totalStops
                      ? "bg-emerald-50 text-emerald-700 ring-emerald-200"
                      : "bg-white text-slate-700 ring-hairline",
                  )}
                >
                  {lane.doneCount}
                  <span className="mx-0.5 font-medium text-slate-400">/</span>
                  <span className="font-semibold text-slate-500">{lane.totalStops}</span>
                </span>
                {onAddAvailability && (
                  <button
                    type="button"
                    onClick={() => onAddAvailability(lane.empno)}
                    aria-label={`Add time off or a meeting for ${lane.name}`}
                    title="Block time — sick day, meeting, recurring early-out…"
                    className="rounded-md p-1 text-slate-400 transition-colors hover:bg-slate-200 hover:text-slate-700"
                  >
                    <CalendarPlus className="h-3.5 w-3.5" />
                  </button>
                )}
              </div>
            );
            return narrow ? (
              <div className="space-y-1">
                {nameBtn}
                <div className="flex items-center justify-between gap-1.5 pl-0.5">{chrome}</div>
              </div>
            ) : (
              <div className="flex items-center justify-between gap-2">
                {nameBtn}
                {chrome}
              </div>
            );
          })()}

          {/* OUT banner (Pegasus-native availability) */}
          {out && (
            <div className="mt-1 flex flex-wrap items-center gap-1.5">
              <div
                className={cn(
                  "inline-flex max-w-full items-center gap-1 truncate rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider",
                  kindClasses(out.kind),
                )}
              >
                Out — {availOutLabel(out)}
                {out.title ? (
                  <span className="normal-case tracking-normal">
                    · {out.title}
                  </span>
                ) : null}
              </div>
              {out.late_notice && (
                <span title="Reported after the company's notice cutoff" className="rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-semibold text-amber-800 ring-1 ring-amber-300/70">
                  Late notice
                </span>
              )}
              {onRebuildDay && hasOpenCalls && (
                <button
                  type="button"
                  onClick={() => onRebuildDay(lane.empno)}
                  title={`Move ${lane.name}'s open calls to other techs`}
                  className="rounded-full border border-rose-200 bg-white px-2 py-0.5 text-[10px] font-semibold text-rose-700 transition-colors hover:bg-rose-50"
                >
                  Rebuild day…
                </button>
              )}
            </div>
          )}
          {/* ESC's legacy Off-Today pseudo ticket (transition period) */}
          {!out && lane.isOff && (
            <div className="mt-1 inline-block rounded-full bg-slate-200 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-slate-600">
              Off today
            </div>
          )}
        </div>

        {/* The day flow: calls + availability blocks in time order. The WHOLE
            column (the outer motion.div) is the drop target now; each item keeps
            data-mins/data-key so a dragged card finds the gap + times itself. The
            brand drop-line shows live where the card will land (Seth 2026-06-24). */}
        <div className="flex-1 space-y-1.5 p-2">
          {items.length === 0 ? (
            targeted ? (
              dropLine
            ) : (
              <div className="py-8 text-center text-[12.5px] font-medium text-slate-400">No stops</div>
            )
          ) : (
            <>
              {items.map((it) => (
                <Fragment key={it.key}>
                  {targeted && dragPreview?.beforeKey === it.key && dropLine}
                  <div data-mins={it.t} data-key={it.key}>{it.node}</div>
                </Fragment>
              ))}
              {targeted && dragPreview?.beforeKey == null && dropLine}
            </>
          )}
        </div>
      </Card>
    </motion.div>
  );
}
